import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, LogOut, Megaphone, Receipt, Shield, Terminal, User, UserCog } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";

export type AccountMenuContext = "customer" | "admin" | "admin_impersonation";

type AccountMenuProps = {
  userEmail: string;
  userName?: string;
  context?: AccountMenuContext;
  roleLabel?: string;
  onExitImpersonation?: () => void;
  showLiveScan?: boolean;
  showPromotions?: boolean;
  sandboxMode?: boolean;
  onToggleSandbox?: (enabled: boolean) => void;
  toggingSandbox?: boolean;
};

const truncateLabel = (value: string) => {
  if (value.length <= 28) return value;
  return `${value.slice(0, 12)}…${value.slice(-12)}`;
};

export function AccountMenu({
  userEmail,
  userName,
  context = "customer",
  roleLabel,
  onExitImpersonation,
  showLiveScan = false,
  showPromotions = false,
  sandboxMode = false,
  onToggleSandbox,
  toggingSandbox = false,
}: AccountMenuProps) {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const labelSource = userName?.trim() || userEmail || "Account";
  const label = useMemo(() => truncateLabel(labelSource), [labelSource]);
  const identityRole = roleLabel ?? (context === "customer" ? "Customer" : undefined);

  const handleSignOut = async () => {
    await signOut();
    navigate("/portal/auth");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <User className="h-4 w-4" />
          <span className="max-w-[160px] truncate">{label}</span>
          {roleLabel && (
            <Badge variant="secondary" className="hidden sm:inline-flex">
              {roleLabel}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <div className="px-3 py-2">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Signed in as</div>
          <div className="text-sm font-medium text-foreground">{userName?.trim() || userEmail || "Account"}</div>
          {userName && userEmail ? (
            <div className="mt-1 text-xs text-muted-foreground">{userEmail}</div>
          ) : null}
          {identityRole && (
            <div className="mt-1 text-[11px] text-muted-foreground">{identityRole}</div>
          )}
        </div>
        <DropdownMenuSeparator />
        {context === "admin_impersonation" ? (
          <>
            <DropdownMenuItem onClick={onExitImpersonation}>
              <UserCog className="h-4 w-4 mr-2" />
              Exit customer view
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem onClick={() => navigate("/portal/account")}>
              <User className="h-4 w-4 mr-2" />
              Account settings
            </DropdownMenuItem>
            {context === "customer" && (
              <DropdownMenuItem onClick={() => navigate("/portal/billing")}>
                <Receipt className="h-4 w-4 mr-2" />
                Billing & subscriptions
              </DropdownMenuItem>
            )}
            {context === "admin" && showLiveScan && (
              <DropdownMenuItem onClick={() => navigate("/portal/autoscan")}>
                <Terminal className="h-4 w-4 mr-2" />
                Live Scan
              </DropdownMenuItem>
            )}
            {context === "admin" && showPromotions && (
              <DropdownMenuItem onClick={() => navigate("/portal/promotions")}>
                <Megaphone className="h-4 w-4 mr-2" />
                Promotions
              </DropdownMenuItem>
            )}
            {context === "admin" && (
              <DropdownMenuItem onClick={() => navigate("/portal/security-audit")}>
                <Shield className="h-4 w-4 mr-2" />
                Security
              </DropdownMenuItem>
            )}
            {context === "admin" && onToggleSandbox && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  disabled={toggingSandbox}
                  onClick={() => onToggleSandbox(!sandboxMode)}
                  className={sandboxMode ? "text-amber-600 dark:text-amber-400" : undefined}
                >
                  <FlaskConical className="h-4 w-4 mr-2" />
                  {toggingSandbox
                    ? sandboxMode ? "Disabling sandbox…" : "Enabling sandbox…"
                    : sandboxMode ? "Disable sandbox mode" : "Enable sandbox mode"}
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
